import { supabase } from '../supabase';

// Team Schedule (031) — events (migration 030, renamed from team_events
// by 043's Event Scope Foundation). Every write goes through a SECURITY
// DEFINER RPC, never a raw insert/update/delete — events has no INSERT/
// UPDATE/DELETE RLS policy at all, matching teams' own "created only
// through create_team_with_admin()" precedent (009).
//
// Phase C.5a — generalized event scope. A row is scoped to exactly one
// Team ('team'), an explicit set of Teams ('specific_teams', via
// event_team_scope — real Team IDs only, never free text or a name
// match), or an entire Division ('entire_division', resolved LIVE
// against a Team's own current division_id, never a snapshot). Every
// function below keeps its existing JS name/shape on purpose — the
// generalized database model does not require a simultaneous frontend
// rename; see 043's own header for the full architecture.

/** Every event applicable to a Team — its own Team-scoped events, plus
 * whatever it inherits from an entire-Division or specific-Teams event
 * (043's list_team_events(), which independently re-verifies the
 * caller's own relationship to teamId, same precedent as
 * listTeamMatchups()). No longer a plain client-side table read — a
 * scope-aware UNION-by-membership can't be expressed as a single
 * PostgREST filter chain. Soonest first; callers split into Upcoming/
 * Past client-side (see utils/teamSchedule.js's todayDateString()). */
export async function listTeamEvents(teamId) {
  const { data, error } = await supabase.rpc('list_team_events', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

/** Management authority only (Team Admin/Head Coach, or Program Owner/
 * Admin, or platform admin for a Team-scoped event; Program Owner/Admin
 * or platform admin ONLY for an entire-Division or specific-Teams event
 * — see 043's own authority-by-scope section) — create_team_event()
 * (030/043). Rejected server-side once season_status is closeout/ended,
 * regardless of what the frontend shows. eventType/homeAway are the raw
 * stored values ('game'|'practice'|'other', 'home'|'away'|'neutral'),
 * never display labels — see utils/teamSchedule.js for those.
 *
 * `scopeType` ('team' | 'specific_teams' | 'entire_division', defaults
 * to 'team' when omitted — every pre-C.5a caller keeps working
 * unchanged), `divisionId` (entire_division only), `teamIds` (specific_
 * teams only — real Team IDs, never resolved by name). teamId remains
 * required for the default 'team' scope and is ignored otherwise. */
export async function createTeamEvent({
  teamId, eventType, title, eventDate, startTime, endTime,
  opponentName, homeAway, locationName, addressLine1, city, state, zipCode, notes,
  scopeType, divisionId, teamIds,
}) {
  const { data, error } = await supabase.rpc('create_team_event', {
    // Explicit `|| null` — create_team_event()'s p_team_id has no SQL
    // default (Postgres forbids a defaulted parameter followed by
    // non-defaulted ones), so it must always be a real, present
    // argument. An omitted/undefined key here would be dropped from the
    // JSON request body entirely and the call would fail outright for
    // an entire-Division/specific-Teams event, which never has a teamId.
    p_team_id: teamId || null,
    p_event_type: eventType,
    p_title: title,
    p_event_date: eventDate,
    p_start_time: startTime,
    p_end_time: endTime || null,
    p_opponent_name: opponentName || null,
    p_home_away: homeAway || null,
    p_location_name: locationName || null,
    p_address_line1: addressLine1 || null,
    p_city: city || null,
    p_state: state || null,
    p_zip_code: zipCode || null,
    p_notes: notes || null,
    p_scope_type: scopeType || 'team',
    p_division_id: divisionId || null,
    p_team_ids: teamIds && teamIds.length > 0 ? teamIds : null,
  });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules as createTeamEvent() — update_team_
 * event() (030/043). Full-row replace, not a partial patch — including
 * scope: omitting scopeType/divisionId/teamIds keeps the event's current
 * scope (043's own update_team_event() re-reads the existing row when
 * p_scope_type is null), passing new ones re-validates and transactionally
 * replaces event_team_scope membership. */
export async function updateTeamEvent({
  eventId, eventType, title, eventDate, startTime, endTime,
  opponentName, homeAway, locationName, addressLine1, city, state, zipCode, notes,
  scopeType, divisionId, teamIds,
}) {
  const { data, error } = await supabase.rpc('update_team_event', {
    p_event_id: eventId,
    p_event_type: eventType,
    p_title: title,
    p_event_date: eventDate,
    p_start_time: startTime,
    p_end_time: endTime || null,
    p_opponent_name: opponentName || null,
    p_home_away: homeAway || null,
    p_location_name: locationName || null,
    p_address_line1: addressLine1 || null,
    p_city: city || null,
    p_state: state || null,
    p_zip_code: zipCode || null,
    p_notes: notes || null,
    p_scope_type: scopeType || null,
    p_division_id: divisionId || null,
    p_team_ids: teamIds && teamIds.length > 0 ? teamIds : null,
  });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules — cancel_team_event() (030). Sets
 * status='cancelled' only; the event row is never deleted and stays
 * visible on the Schedule. */
export async function cancelTeamEvent(eventId) {
  const { data, error } = await supabase.rpc('cancel_team_event', { p_event_id: eventId });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules — delete_team_event() (030). Permanent
 * — for erroneous/test entries only, distinct from cancelTeamEvent()
 * above which always preserves the row. */
export async function deleteTeamEvent(eventId) {
  const { error } = await supabase.rpc('delete_team_event', { p_event_id: eventId });
  if (error) throw error;
}

/** Phase C.3 — marks a Game completed with its final score. Same
 * authority/lifecycle rules as the RPCs above (041). Only a Game, never
 * Practice/Other; refuses a cancelled game. Safe to call again on an
 * already-completed game to correct a mistyped score. */
export async function completeTeamEvent({ eventId, teamScore, opponentScore }) {
  const { data, error } = await supabase.rpc('complete_team_event', {
    p_event_id: eventId,
    p_team_score: teamScore,
    p_opponent_score: opponentScore,
  });
  if (error) throw error;
  return data;
}

/** Phase C.3 — marks a Scheduled Game postponed (041). Same authority/
 * lifecycle rules. Only a Game, only from 'scheduled' — update a
 * postponed game's date via updateTeamEvent() once a new one is known. */
export async function postponeTeamEvent(eventId) {
  const { data, error } = await supabase.rpc('postpone_team_event', { p_event_id: eventId });
  if (error) throw error;
  return data;
}

/** Phase C.5d — the inverse of completeTeamEvent(): clears a completed
 * Game's result and returns it to 'scheduled' (status='scheduled',
 * team_score/opponent_score both null), same row/id, same authority
 * tier as completing it (046's reopen_team_event()). For "the result
 * was entered against the wrong Game entirely" — a wrong SCORE on the
 * right Game is still corrected via completeTeamEvent() again, unchanged. */
export async function reopenTeamEvent(eventId) {
  const { data, error } = await supabase.rpc('reopen_team_event', { p_event_id: eventId });
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════════════════════════
// Phase C.4 — internal Team-vs-Team matchups (team_matchups, 042). A
// SEPARATE table from team_events, not an extension of it — see 042's own
// header for the full architecture reasoning. Reads go through a
// SECURITY DEFINER RPC (list_team_matchups), not a raw client select —
// team_matchups' own rows are correctly RLS-visible to either
// participating Team, but an embedded client-side join for "the
// opponent's name" would silently return null when the caller has no
// direct RLS relationship to the OTHER Team's own `teams` row (e.g. a
// Team A Head Coach with no Program authority has no visibility into
// Team B's row at all under teams' own existing per-Team RLS). This RPC
// independently re-verifies the caller's relationship to p_team_id, then
// safely joins in both Teams' names, returning rows already
// perspective-normalized (opponent_name/home_away/team_score/
// opponent_score) — the exact same shape listTeamEvents() rows have, so
// TeamSchedulePage.js can merge both arrays into one display list with
// no per-row branching beyond a small `kind` discriminator.
// ═══════════════════════════════════════════════════════════════════════

/** Every internal matchup involving this Team, soonest first, already
 * oriented to this Team's own perspective (042's list_team_matchups(),
 * evolved by 045 to also return `notes` — private operational
 * information, exposed here only because this is the authenticated,
 * already-authority-gated read; never present in any public matchup
 * projection). */
export async function listTeamMatchups(teamId) {
  const { data, error } = await supabase.rpc('list_team_matchups', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

/** Every OTHER active Team sharing this Team's Division — id/name only —
 * powers the "FFN Team" opponent selector. Cannot reuse the public
 * Division-teams RPC (038) here: that one requires the Division to
 * already be public-safe, which would break authoring for a Program
 * that isn't public yet (042's list_division_sibling_teams()). */
export async function listDivisionSiblingTeams(teamId) {
  const { data, error } = await supabase.rpc('list_division_sibling_teams', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

/** Creates a shared matchup. teamId is the creating/owning perspective
 * (whichever Team's Schedule this was added from) — homeAway is relative
 * to teamId, same picker as createTeamEvent()'s own Home/Away/Neutral
 * control. Server independently validates: different Teams, both active,
 * same Division, and Team Admin/Head Coach-or-Program-authority over
 * EITHER Team (042's create_team_matchup()) — client-side filtering of
 * the opponent selector is a convenience only, never trusted alone. */
export async function createTeamMatchup({ teamId, opponentTeamId, eventDate, startTime, homeAway, notes }) {
  const { data, error } = await supabase.rpc('create_team_matchup', {
    p_team_id: teamId,
    p_opponent_team_id: opponentTeamId,
    p_event_date: eventDate,
    p_start_time: startTime,
    p_home_away: homeAway,
    p_notes: notes || null,
  });
  if (error) throw error;
  return data;
}

/** Updates event_date/start_time/site/notes — team_a_id/team_b_id can
 * NEVER be changed via this call (042's own explicit design: a matchup
 * is never reassigned to different Teams). perspectiveTeamId must be one
 * of the matchup's own two Teams; homeAway is relative to it. `notes`
 * (045) is always sent, even when unchanged/empty — like update_team_
 * event()'s own p_notes, omitting it would clear it, so the caller must
 * always pass the current value (preloaded on Edit). */
export async function updateTeamMatchup({ matchupId, eventDate, startTime, perspectiveTeamId, homeAway, notes }) {
  const { data, error } = await supabase.rpc('update_team_matchup', {
    p_matchup_id: matchupId,
    p_event_date: eventDate,
    p_start_time: startTime,
    p_perspective_team_id: perspectiveTeamId,
    p_home_away: homeAway,
    p_notes: notes || null,
  });
  if (error) throw error;
  return data;
}

/** Marks a matchup completed. teamScore/opponentScore are relative to
 * perspectiveTeamId, same shape as completeTeamEvent() — reuses the same
 * CompleteGameDialog labels/UX. Safe to call again to correct a score;
 * never touches which two Teams are playing. */
export async function completeTeamMatchup({ matchupId, perspectiveTeamId, teamScore, opponentScore }) {
  const { data, error } = await supabase.rpc('complete_team_matchup', {
    p_matchup_id: matchupId,
    p_perspective_team_id: perspectiveTeamId,
    p_team_score: teamScore,
    p_opponent_score: opponentScore,
  });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules as postponeTeamEvent() — postpone_team_
 * matchup() (042). Only from 'scheduled'. */
export async function postponeTeamMatchup(matchupId) {
  const { data, error } = await supabase.rpc('postpone_team_matchup', { p_matchup_id: matchupId });
  if (error) throw error;
  return data;
}

/** Phase C.5d — the inverse of completeTeamMatchup(): clears a completed
 * matchup's result and returns it to 'scheduled' (team_a_score/team_b_
 * score both null), same row/id, same authority tier as completing it
 * (046's reopen_team_matchup()). */
export async function reopenTeamMatchup(matchupId) {
  const { data, error } = await supabase.rpc('reopen_team_matchup', { p_matchup_id: matchupId });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules as cancelTeamEvent() — cancel_team_
 * matchup() (042). Sets status='cancelled' only; the row is preserved. */
export async function cancelTeamMatchup(matchupId) {
  const { data, error } = await supabase.rpc('cancel_team_matchup', { p_matchup_id: matchupId });
  if (error) throw error;
  return data;
}

/** Program Owner/Admin or platform admin ONLY — deliberately NOT Team
 * Admin/Head Coach from either participating Team (042's own approved
 * architecture change, section 3): erasing a shared result affects the
 * other Team's history too, a more privileged action than correcting it. */
export async function deleteTeamMatchup(matchupId) {
  const { error } = await supabase.rpc('delete_team_matchup', { p_matchup_id: matchupId });
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════
// Phase C.5b — recurring Practice/Other (event_series, 044). Materialized
// occurrences: every generated row is a real, independent `events` row
// with series_id set, read by the exact same listTeamEvents() above —
// nothing new to fetch. Recurrence never applies to a Game.
// ═══════════════════════════════════════════════════════════════════════

/** Creates ONE event_series row and materializes one events row per
 * matching weekday from eventDate through untilDate (inclusive) —
 * create_recurring_event() (044). weekdays is 0=Sun..6=Sat. Same scope
 * parameters as createTeamEvent() ('team'|'specific_teams'|
 * 'entire_division'); eventType must be 'practice' or 'other'. Returns
 * every created occurrence row. */
export async function createRecurringEvent({
  eventType, title, eventDate, startTime, untilDate, weekdays, endTime,
  locationName, addressLine1, city, state, zipCode, notes,
  scopeType, teamId, divisionId, teamIds,
}) {
  const { data, error } = await supabase.rpc('create_recurring_event', {
    p_event_type: eventType,
    p_title: title,
    p_start_date: eventDate,
    p_start_time: startTime,
    p_until_date: untilDate,
    p_weekdays: weekdays,
    p_end_time: endTime || null,
    p_location_name: locationName || null,
    p_address_line1: addressLine1 || null,
    p_city: city || null,
    p_state: state || null,
    p_zip_code: zipCode || null,
    p_notes: notes || null,
    p_scope_type: scopeType || 'team',
    p_team_id: teamId || null,
    p_division_id: divisionId || null,
    p_team_ids: teamIds && teamIds.length > 0 ? teamIds : null,
  });
  if (error) throw error;
  return data;
}

/** "This + future" / "Entire series" field edits — update_recurring_
 * series() (044). eventId anchors WHICH occurrence/series is being
 * edited and (for 'this_and_future') where the boundary falls. Passing
 * `weekdays` different from the series' own current pattern always
 * triggers a series split server-side, regardless of applyTo — see
 * 044's own header. Never used for 'this' (a single occurrence reuses
 * updateTeamEvent() directly — see GameFormDialogs.js). */
export async function updateRecurringSeries({
  eventId, applyTo, title, startTime, endTime,
  locationName, addressLine1, city, state, zipCode, notes,
  scopeType, teamId, divisionId, teamIds, weekdays,
}) {
  const { error } = await supabase.rpc('update_recurring_series', {
    p_event_id: eventId,
    p_apply_to: applyTo,
    p_title: title,
    p_start_time: startTime,
    p_end_time: endTime || null,
    p_location_name: locationName || null,
    p_address_line1: addressLine1 || null,
    p_city: city || null,
    p_state: state || null,
    p_zip_code: zipCode || null,
    p_notes: notes || null,
    p_scope_type: scopeType || null,
    p_team_id: teamId || null,
    p_division_id: divisionId || null,
    p_team_ids: teamIds && teamIds.length > 0 ? teamIds : null,
    p_weekdays: weekdays && weekdays.length > 0 ? weekdays : null,
  });
  if (error) throw error;
}

/** "This + future" / "Entire series" Cancel (action: 'cancel') or
 * Delete (action: 'exclude') — apply_recurring_action() (044). "This
 * occurrence only" for either action reuses cancelTeamEvent()/
 * deleteTeamEvent() directly, unchanged — a single row is a single row.
 * Returns the number of occurrences affected. */
export async function applyRecurringAction({ eventId, applyTo, action }) {
  const { data, error } = await supabase.rpc('apply_recurring_action', {
    p_event_id: eventId,
    p_apply_to: applyTo,
    p_action: action,
  });
  if (error) throw error;
  return data;
}
